'use client';

import type { PlanComparisonRow, PlanTier } from './PlansComparison';
import type { ApiPlan, ApiSubscription } from '@/types/usage';

type PlansTranslator = (key: string) => string;

type BuildPlansComparisonOptions = {
  includeFreePlanColumn?: boolean;
};

type LocalizedPlansComparison = {
  plans: PlanTier[];
  comparisonRows: PlanComparisonRow[];
  customSetupNote: {
    title: string;
    description: string;
  };
  currentSubscriptionPlanId?: string;
};

function isCustomPlan(plan: Pick<ApiPlan, 'type'>) {
  return plan.type === 'custom_plan';
}

function usesCustomDisplayValues(plan: { type?: string; code: string }) {
  return plan.type === 'custom_plan' && plan.code === 'custom_plan';
}

export function formatPlanAmount(amount?: string | null) {
  if (!amount) return '$0';

  const parsed = Number(amount);
  if (Number.isNaN(parsed)) return amount;

  return `$${Math.floor(parsed).toLocaleString()}`;
}

export function formatPlanPeriod(period?: string | null) {
  if (!period) return '';

  return `/${period}`;
}

export function buildLocalizedPlansComparison(
  plans: ApiPlan[],
  subscription: ApiSubscription,
  t: PlansTranslator,
  options: BuildPlansComparisonOptions = {}
): LocalizedPlansComparison {
  const includeFreePlanColumn = options.includeFreePlanColumn ?? false;
  const currentSubscriptionPlanId = subscription.plan.id;
  const isFreePlanCurrent = subscription.plan.type === 'free_plan';
  const currentPlanSequence = subscription.plan.sequence;
  const isCanceledSubscription =
    subscription.status === 'canceled' || subscription.status === 'cancelled';
  const isPaymentFailed = subscription.status === 'payment_failed';

  const visiblePlans = [...plans]
    .sort((a, b) => a.sequence - b.sequence)
    .filter(plan =>
      includeFreePlanColumn
        ? plan.type !== 'free_plan' || isFreePlanCurrent
        : plan.type !== 'free_plan'
    );

  const mappedPlans = visiblePlans.map(plan => {
    const isCurrent = plan.id === currentSubscriptionPlanId;
    const isCustom = isCustomPlan(plan);
    const showCustomValues = usesCustomDisplayValues(plan);
    const isFree = plan.type === 'free_plan';
    const ctaLabel = isFree
      ? undefined
      : isCustom
        ? isCurrent
          ? undefined
          : t('talkToUs')
        : isCanceledSubscription
          ? t('subscribe')
          : plan.sequence < currentPlanSequence
            ? t('downgrade')
            : t('upgrade');

    return {
      id: plan.id,
      code: plan.code,
      type: plan.type,
      sequence: plan.sequence,
      name: plan.name,
      price: showCustomValues ? t('custom') : formatPlanAmount(plan.amount),
      period: showCustomValues ? '' : formatPlanPeriod(plan.recurring_type),
      totalCredits: plan.total_credits,
      totalUsers: plan.total_users,
      totalPhoneLines: plan.total_phone_lines,
      totalMailboxes: plan.total_mailboxes,
      totalLinkedinAccounts: plan.total_linkedin_accounts,
      isCurrent,
      status: isCurrent ? subscription.status : undefined,
      isCustom,
      disabled: isCurrent && !isCanceledSubscription && !isPaymentFailed,
      ctaLabel,
    };
  });

  const valueOrCustom = (plan: PlanTier, value?: string | number | null) => {
    if (usesCustomDisplayValues(plan)) {
      return t('custom');
    }

    if (value !== undefined && value !== null) {
      return typeof value === 'number' ? value.toLocaleString() : value;
    }

    return '-';
  };

  return {
    currentSubscriptionPlanId,
    plans: mappedPlans,
    comparisonRows: [
      {
        label: t('creditsIncluded'),
        values: mappedPlans.map(plan => valueOrCustom(plan, plan.totalCredits)),
      },
      {
        label: t('users'),
        values: mappedPlans.map(plan => valueOrCustom(plan, plan.totalUsers)),
      },
      {
        label: t('iMessageLines'),
        values: mappedPlans.map(plan => valueOrCustom(plan, plan.totalPhoneLines)),
      },
      {
        label: t('mailboxes'),
        values: mappedPlans.map(plan => valueOrCustom(plan, plan.totalMailboxes)),
      },
      {
        label: t('linkedInAccounts'),
        values: mappedPlans.map(plan => valueOrCustom(plan, plan.totalLinkedinAccounts)),
      },
    ],
    customSetupNote: {
      title: t('customSetupTitle'),
      description: t('customSetupDescription'),
    },
  };
}

export function buildPlanTierFromSubscription(subscription: ApiSubscription): PlanTier {
  const showCustomValues = usesCustomDisplayValues(subscription.plan);

  return {
    id: subscription.plan.id,
    code: subscription.plan.code,
    type: subscription.plan.type,
    sequence: subscription.plan.sequence,
    name: subscription.plan.name,
    price: showCustomValues ? 'Custom' : formatPlanAmount(subscription.plan.amount),
    period: showCustomValues ? '' : formatPlanPeriod(subscription.plan.recurring_type),
    isCustom: subscription.plan.type === 'custom_plan',
    totalCredits: subscription.plan.total_credits,
    totalUsers: subscription.plan.total_users,
    totalPhoneLines: subscription.plan.total_phone_lines,
    totalMailboxes: subscription.plan.total_mailboxes,
    totalLinkedinAccounts: subscription.plan.total_linkedin_accounts,
    status: subscription.status,
  };
}
